import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { ReportHeader } from '../../components/ReportHeader';
import { FilterPanel } from '../../components/FilterPanel';
import { SortableTable } from '../../components/SortableTable';
import { ExportButton } from '../../components/ExportButton';
import { Pagination } from '../../components/Pagination';

const API = process.env.REACT_APP_BACKEND_URL;

// ── helpers ──────────────────────────────────────────────────────────────────
function fmt(n)    { return (n ?? 0).toLocaleString(); }
function fmtPct(v) { return `${Number(v ?? 0).toFixed(1)}%`; }

const PROGRAMS = [
  { value: '',                              label: 'All' },
  { value: 'Jumpstart Copilot',             label: 'Jumpstart Copilot' },
  { value: 'Jumpstart Converged Communications', label: 'Converged Comms' },
  { value: 'Jumpstart Cloud Endpoints',     label: 'Cloud Endpoints' },
  { value: 'Jumpstart Business Central',   label: 'Business Central' },
];

const PROGRAM_COLORS = {
  'Jumpstart Copilot':                  '#6366f1',
  'Jumpstart Converged Communications': '#0ea5e9',
  'Jumpstart Cloud Endpoints':          '#10b981',
  'Jumpstart Business Central':         '#f59e0b',
};

// ── KPI card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, accent, testId, children }) {
  return (
    <div
      data-testid={testId}
      className="stat-card"
      style={{ borderTop: `3px solid ${accent}` }}
    >
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{sub}</div>}
      {children}
    </div>
  );
}

// ── Activation Rate card with inline progress bar ─────────────────────────────
function ActivationKpiCard({ kpis, accent, loading, error }) {
  const active = kpis?.active_partners ?? 0;
  const total  = kpis?.total_partners ?? 0;
  const rate   = kpis?.activation_rate ?? 0;
  const pct    = Math.min(100, rate);

  return (
    <div
      data-testid="kpi-activation-rate"
      className="stat-card"
      style={{ borderTop: `3px solid ${accent}` }}
    >
      <div className="stat-label">Activation Rate</div>
      <div className="stat-value">
        {loading ? '…' : error ? '—' : fmtPct(rate)}
      </div>
      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
        {loading ? 'Loading…' : error ? 'Retry above' : `${fmt(active)} / ${fmt(total)} partners activated`}
      </div>
      <div style={{
        marginTop: 8,
        height: 6,
        borderRadius: 3,
        background: '#e2e8f0',
        overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${loading || error ? 0 : pct}%`,
          background: accent,
          borderRadius: 3,
          transition: 'width 0.4s ease',
        }} />
      </div>
    </div>
  );
}

// ── Programme summary card ────────────────────────────────────────────────────
function ProgramCard({ data, isSelected }) {
  const color = PROGRAM_COLORS[data.program] || '#6366f1';
  return (
    <div
      data-testid={`prog-card-${data.program.replace(/\s+/g, '-').toLowerCase()}`}
      style={{
        border: `2px solid ${isSelected ? color : '#e2e8f0'}`,
        borderRadius: 10,
        padding: '14px 16px',
        background: isSelected ? `${color}08` : '#fff',
        transition: 'border-color 0.15s, background 0.15s',
        flex: '1 1 200px',
        minWidth: 200,
      }}
    >
      <div style={{
        fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase',
        letterSpacing: '0.05em', marginBottom: 8,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {data.program}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', fontSize: 12 }}>
        <div>
          <div style={{ color: '#94a3b8', fontSize: 10 }}>PARTNERS</div>
          <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 15 }}>{fmt(data.total_partners)}</div>
        </div>
        <div>
          <div style={{ color: '#94a3b8', fontSize: 10 }}>ACTIVE</div>
          <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 15 }}>{fmt(data.active_partners)}</div>
        </div>
        <div>
          <div style={{ color: '#94a3b8', fontSize: 10 }}>ENROLMENTS</div>
          <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 15 }}>{fmt(data.total_enrolments)}</div>
        </div>
        <div>
          <div style={{ color: '#94a3b8', fontSize: 10 }}>COMPLETION</div>
          <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 15 }}>{fmtPct(data.completion_rate)}</div>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 style={{
      fontSize: 14, fontWeight: 700, color: '#1e293b',
      margin: '24px 0 10px', letterSpacing: '-0.01em',
    }}>
      {children}
    </h2>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ activated }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 9px',
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 600,
      background: activated ? '#dcfce7' : '#fef3c7',
      color: activated ? '#166534' : '#92400e',
    }}>
      {activated ? 'Active' : 'Not yet activated'}
    </span>
  );
}

// ── Column definitions ─────────────────────────────────────────────────────────

const PARTNERS_COLS = [
  { key: 'partner_name',   label: 'Partner Name',    sortable: true, maxWidth: 220, wrap: true },
  { key: 'domain',         label: 'Domain',           sortable: true },
  { key: 'program',        label: 'Sub-Programme',    sortable: true },
  { key: 'partner_group',  label: 'Partner Group',    sortable: true,
    render: v => v || <span style={{ color: '#94a3b8' }}>—</span> },
  { key: 'learners',       label: 'Learners',         sortable: true, align: 'right' },
  { key: 'enrolments',     label: 'Enrolments',       sortable: true, align: 'right' },
  { key: 'completions',    label: 'Completions',      sortable: true, align: 'right' },
  { key: 'completion_rate',label: 'Completion %',     sortable: true, align: 'right',
    render: v => fmtPct(v) },
  { key: 'activated',      label: 'Status',           sortable: false,
    render: v => <StatusBadge activated={v} /> },
];

const PARTNERS_EXPORT_COLS = [
  { key: 'partner_name',   label: 'Partner Name' },
  { key: 'domain',         label: 'Domain' },
  { key: 'program',        label: 'Sub-Programme' },
  { key: 'partner_group',  label: 'Partner Group' },
  { key: 'learners',       label: 'Learners' },
  { key: 'enrolments',     label: 'Enrolments' },
  { key: 'completions',    label: 'Completions' },
  { key: 'completion_rate',label: 'Completion Rate %' },
  { key: 'status',         label: 'Status' },
];

const COURSES_COLS = [
  { key: 'course_name',    label: 'Course Name',      sortable: true, maxWidth: 320, wrap: true },
  { key: 'solution_area',  label: 'Solution Area',    sortable: true },
  { key: 'enrolments',     label: 'Enrolments',       sortable: true, align: 'right' },
  { key: 'completions',    label: 'Completions',      sortable: true, align: 'right' },
  { key: 'completion_rate',label: 'Completion %',     sortable: true, align: 'right',
    render: v => fmtPct(v) },
  { key: 'unique_learners',label: 'Unique Learners',  sortable: true, align: 'right' },
];

const PARTNERS_FILTER_CONFIG = [
  { key: 'search',        label: 'Search',        type: 'text-search' },
  { key: 'partner_group', label: 'Partner Group', type: 'multiselect',
    options: ['ESI_MPL_GSI', 'CSP', 'Unclassified'] },
  { key: 'activated',     label: 'Activation',    type: 'toggle',
    options: ['Activated only', 'Not yet activated'] },
];

// ── Main page ─────────────────────────────────────────────────────────────────
export default function JumpstartReport() {
  // Period from ReportHeader
  const [period, setPeriod]             = useState({ fyLabel: '', startDate: '', endDate: '' });

  // Programme pill filter
  const [selectedProgram, setSelectedProgram] = useState('');

  // KPIs
  const [kpis, setKpis]                 = useState(null);
  const [kpisLoading, setKpisLoading]   = useState(false);
  const [kpisError, setKpisError]       = useState(false);

  // Summary by programme (all-time, no period filter)
  const [summary, setSummary]           = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Trend
  const [trendData, setTrendData]       = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);

  // Active tab
  const [activeTab, setActiveTab]       = useState('partners');

  // Partners tab
  const [partners, setPartners]         = useState({ total: 0, data: [] });
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [partnersPage, setPartnersPage]       = useState(1);
  const [partnersPageSize, setPartnersPageSize] = useState(50);
  const [partnerSort, setPartnerSort]   = useState([{ key: 'enrolments', direction: 'desc' }]);
  const [partnerFilters, setPartnerFilters] = useState({ search: '', partner_group: [], activated: '' });

  // Courses tab
  const [courses, setCourses]           = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [courseSort, setCourseSort]     = useState([{ key: 'enrolments', direction: 'desc' }]);

  // ── Fetch helpers ─────────────────────────────────────────────────────────

  const periodParams = useCallback(() => ({
    fy_label:   period.fyLabel   || undefined,
    start_date: period.startDate || undefined,
    end_date:   period.endDate   || undefined,
  }), [period]);

  // KPIs
  const fetchKpis = useCallback(async () => {
    if (!period.fyLabel) return;
    setKpisLoading(true);
    setKpisError(false);
    try {
      const { data } = await axios.get(`${API}/api/jumpstart/kpis`, {
        params: { ...periodParams(), program: selectedProgram || undefined },
        timeout: 60000,
      });
      setKpis(data);
    } catch (e) {
      console.error('Jumpstart KPIs error:', e);
      setKpisError(true);
    } finally {
      setKpisLoading(false);
    }
  }, [period, selectedProgram, periodParams]);

  // Summary by programme (all-time, no period)
  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/jumpstart/summary-by-program`);
      setSummary(data);
    } catch (e) {
      console.error('Jumpstart summary error:', e);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  // Trend
  const fetchTrend = useCallback(async () => {
    if (!period.fyLabel) return;
    setTrendLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/jumpstart/trend`, {
        params: { ...periodParams(), program: selectedProgram || undefined },
      });
      setTrendData(data);
    } catch (e) {
      console.error('Jumpstart trend error:', e);
    } finally {
      setTrendLoading(false);
    }
  }, [period, selectedProgram, periodParams]);

  // Partners
  const fetchPartners = useCallback(async () => {
    if (!period.fyLabel) return;
    setPartnersLoading(true);
    try {
      const { search, partner_group, activated } = partnerFilters;
      const sortPrimary = partnerSort[0] || { key: 'enrolments', direction: 'desc' };
      const { data } = await axios.get(`${API}/api/jumpstart/partners`, {
        params: {
          ...periodParams(),
          program:        selectedProgram || undefined,
          search:         search || undefined,
          partner_group:  partner_group?.length ? partner_group.join(',') : undefined,
          activated:      activated === 'Activated only'       ? 'activated_only'
                        : activated === 'Not yet activated'    ? 'not_activated'
                        : undefined,
          sort_by:   sortPrimary.key,
          sort_dir:  sortPrimary.direction,
          page:      partnersPage,
          page_size: partnersPageSize,
        },
      });
      setPartners(data);
    } catch (e) {
      console.error('Jumpstart partners error:', e);
    } finally {
      setPartnersLoading(false);
    }
  }, [period, selectedProgram, partnerFilters, partnerSort, partnersPage, partnersPageSize, periodParams]);

  // Courses
  const fetchCourses = useCallback(async () => {
    setCoursesLoading(true);
    try {
      const sortPrimary = courseSort[0] || { key: 'enrolments', direction: 'desc' };
      const { data } = await axios.get(`${API}/api/jumpstart/courses`, {
        params: { sort_by: sortPrimary.key, sort_dir: sortPrimary.direction },
      });
      setCourses(data);
    } catch (e) {
      console.error('Jumpstart courses error:', e);
    } finally {
      setCoursesLoading(false);
    }
  }, [courseSort]);

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchKpis();    }, [fetchKpis]);
  useEffect(() => { fetchTrend();   }, [fetchTrend]);
  useEffect(() => { fetchPartners(); }, [fetchPartners]);
  useEffect(() => { fetchCourses();  }, [fetchCourses]);

  // Reset page on filter/sort change
  const handlePartnerFilterChange = useCallback((filters) => {
    setPartnerFilters(filters);
    setPartnersPage(1);
  }, []);

  const handlePartnerSortChange = useCallback((sorts) => {
    setPartnerSort(sorts);
    setPartnersPage(1);
  }, []);

  const handleProgramChange = useCallback((prog) => {
    setSelectedProgram(prog);
    setPartnersPage(1);
  }, []);

  // Export function
  const exportPartners = useCallback(async () => {
    const { search, partner_group, activated } = partnerFilters;
    const sortPrimary = partnerSort[0] || { key: 'enrolments', direction: 'desc' };
    const { data } = await axios.get(`${API}/api/jumpstart/export/partners`, {
      params: {
        ...periodParams(),
        program:       selectedProgram || undefined,
        search:        search || undefined,
        partner_group: partner_group?.length ? partner_group.join(',') : undefined,
        activated:     activated === 'Activated only'    ? 'activated_only'
                     : activated === 'Not yet activated' ? 'not_activated'
                     : undefined,
        sort_by:  sortPrimary.key,
        sort_dir: sortPrimary.direction,
      },
    });
    return data;
  }, [partnerFilters, partnerSort, period, selectedProgram, periodParams]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div data-testid="jumpstart-report">
      {/* Sticky header */}
      <ReportHeader
        reportTitle="Jumpstart Report"
        primaryTable="enrollments"
        onChange={setPeriod}
      />

      <div style={{ padding: '16px 24px' }}>

        {/* Programme pill filter bar */}
        <div
          data-testid="programme-filter-bar"
          style={{
            display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20,
            padding: '10px 14px',
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b',
            textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 4 }}>
            Programme:
          </span>
          {PROGRAMS.map(prog => (
            <button
              key={prog.value}
              data-testid={`prog-pill-${prog.value || 'all'}`}
              onClick={() => handleProgramChange(prog.value)}
              style={{
                padding: '4px 14px',
                borderRadius: 20,
                border: `1.5px solid ${selectedProgram === prog.value
                  ? (PROGRAM_COLORS[prog.value] || '#6366f1')
                  : '#e2e8f0'}`,
                background: selectedProgram === prog.value
                  ? (PROGRAM_COLORS[prog.value] || '#6366f1')
                  : '#fff',
                color: selectedProgram === prog.value ? '#fff' : '#475569',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.12s',
              }}
            >
              {prog.label}
            </button>
          ))}
        </div>

        {/* KPI cards */}
        <div
          data-testid="kpi-cards"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}
        >
          {kpisError && (
            <div
              style={{
                gridColumn: '1 / -1',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 8,
                padding: '10px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                fontSize: 13,
                color: '#991b1b',
              }}
            >
              <span>KPI data failed to load (query timeout).</span>
              <button
                onClick={fetchKpis}
                style={{
                  padding: '4px 12px',
                  borderRadius: 6,
                  border: '1px solid #fca5a5',
                  background: '#fff',
                  color: '#dc2626',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Retry
              </button>
            </div>
          )}
          <KpiCard
            label="Total Partners"
            value={kpisLoading ? '…' : kpisError ? '—' : fmt(kpis?.total_partners)}
            accent="#6366f1"
            testId="kpi-total-partners"
          />
          <KpiCard
            label="Active Partners"
            value={kpisLoading ? '…' : kpisError ? '—' : fmt(kpis?.active_partners)}
            sub={kpisLoading || kpisError ? '' : `of ${fmt(kpis?.total_partners)} total`}
            accent="#0ea5e9"
            testId="kpi-active-partners"
          />
          <ActivationKpiCard kpis={kpisLoading || kpisError ? null : kpis} accent="#10b981" loading={kpisLoading} error={kpisError} />
          <KpiCard
            label="Total Enrolments"
            value={kpisLoading ? '…' : kpisError ? '—' : fmt(kpis?.total_enrolments)}
            accent="#f59e0b"
            testId="kpi-total-enrolments"
          />
          <KpiCard
            label="Total Completions"
            value={kpisLoading ? '…' : kpisError ? '—' : fmt(kpis?.total_completions)}
            accent="#8b5cf6"
            testId="kpi-total-completions"
          />
          <KpiCard
            label="Completion Rate"
            value={kpisLoading ? '…' : kpisError ? '—' : fmtPct(kpis?.completion_rate)}
            accent="#ef4444"
            testId="kpi-completion-rate"
          />
        </div>

        {/* Programme breakdown cards (all-time) */}
        <SectionTitle>Programme Breakdown — All Time</SectionTitle>
        <div
          data-testid="programme-breakdown-cards"
          style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}
        >
          {summaryLoading
            ? PROGRAMS.filter(p => p.value).map(p => (
                <div key={p.value} style={{
                  flex: '1 1 200px', minWidth: 200, height: 112,
                  border: '1px solid #e2e8f0', borderRadius: 10,
                  background: '#f8fafc', animation: 'shimmer 1.6s ease-in-out infinite',
                }} />
              ))
            : summary.map(s => (
                <ProgramCard
                  key={s.program}
                  data={s}
                  isSelected={selectedProgram === s.program}
                />
              ))
          }
        </div>

        {/* Monthly trend chart */}
        <SectionTitle>Monthly Trend</SectionTitle>
        <div
          data-testid="jumpstart-trend-chart"
          style={{
            background: '#fff', border: '1px solid #e2e8f0',
            borderRadius: 10, padding: '16px 8px', marginBottom: 24,
          }}
        >
          {trendLoading ? (
            <div style={{ height: 260, display: 'flex', alignItems: 'center',
              justifyContent: 'center', color: '#94a3b8', fontSize: 13 }}>
              Loading trend data…
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={trendData} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone" dataKey="enrolments" name="Enrolments"
                  stroke="#6366f1" strokeWidth={2} dot={false} activeDot={{ r: 4 }}
                />
                <Line
                  type="monotone" dataKey="completions" name="Completions"
                  stroke="#10b981" strokeWidth={2} dot={false} activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Tabs: Partners | Courses */}
        <div data-testid="jumpstart-tabs" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0' }}>
            {['partners', 'courses'].map(tab => (
              <button
                key={tab}
                data-testid={`tab-${tab}`}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '8px 20px',
                  fontSize: 13, fontWeight: 600,
                  background: 'none', border: 'none',
                  borderBottom: `2px solid ${activeTab === tab ? '#6366f1' : 'transparent'}`,
                  marginBottom: -2,
                  color: activeTab === tab ? '#6366f1' : '#64748b',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  transition: 'color 0.1s',
                }}
              >
                {tab === 'partners' ? 'Partners' : 'Courses'}
              </button>
            ))}
          </div>
        </div>

        {/* ── Partners Tab ─────────────────────────────────────────────────── */}
        {activeTab === 'partners' && (
          <div data-testid="partners-tab">
            {/* FilterPanel */}
            <div style={{ marginBottom: 12 }}>
              <FilterPanel
                filterConfig={PARTNERS_FILTER_CONFIG}
                activeFilters={partnerFilters}
                onChange={handlePartnerFilterChange}
              />
            </div>

            {/* Table controls */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <ExportButton
                fetchAllFn={exportPartners}
                filename={`jumpstart-partners_${period.fyLabel || 'all-time'}`}
                columns={PARTNERS_EXPORT_COLS}
                label="Export Partners CSV"
              />
            </div>

            <div style={{
              background: '#fff', border: '1px solid #e2e8f0',
              borderRadius: 8, overflow: 'hidden',
            }}>
              <SortableTable
                columns={PARTNERS_COLS}
                data={partners.data}
                defaultSort={{ key: 'enrolments', direction: 'desc' }}
                onSortChange={handlePartnerSortChange}
                loading={partnersLoading}
              />
            </div>

            <div style={{ marginTop: 12 }}>
              <Pagination
                totalCount={partners.total}
                pageSize={partnersPageSize}
                currentPage={partnersPage}
                onPageChange={setPartnersPage}
                onPageSizeChange={size => { setPartnersPageSize(size); setPartnersPage(1); }}
              />
            </div>
          </div>
        )}

        {/* ── Courses Tab ──────────────────────────────────────────────────── */}
        {activeTab === 'courses' && (
          <div data-testid="courses-tab">
            <div style={{
              background: '#fff', border: '1px solid #e2e8f0',
              borderRadius: 8, overflow: 'hidden',
            }}>
              <SortableTable
                columns={COURSES_COLS}
                data={courses}
                defaultSort={{ key: 'enrolments', direction: 'desc' }}
                onSortChange={setCourseSort}
                loading={coursesLoading}
              />
            </div>
          </div>
        )}
      </div>

      {/* Shimmer animation for skeleton loading */}
      <style>{`
        @keyframes shimmer {
          0%   { opacity: 1; }
          50%  { opacity: 0.4; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
